import { useEffect, useState } from "react";

// ── Hook: encapsulates the beforeinstallprompt / appinstalled lifecycle ──────
export function usePWAInstall() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [canInstall, setCanInstall] = useState(false);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    const isStandalone =
      window.matchMedia?.("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (isStandalone) setInstalled(true);

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setCanInstall(true);
    };
    const onInstalled = () => {
      setInstalled(true);
      setCanInstall(false);
      setDeferredPrompt(null);
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const promptInstall = async () => {
    if (!deferredPrompt) return false;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    setCanInstall(false);
    return outcome === "accepted";
  };

  return { canInstall, installed, promptInstall };
}

// ── Button: only renders when the browser has actually offered installation ─
export default function PWAInstallButton({ C }) {
  const { canInstall, installed, promptInstall } = usePWAInstall();
  const [justInstalled, setJustInstalled] = useState(false);
  const [hover, setHover] = useState(false);

  const handleClick = async () => {
    const accepted = await promptInstall();
    if (accepted) {
      setJustInstalled(true);
      setTimeout(() => setJustInstalled(false), 4000);
    }
  };

  if (installed && !justInstalled) return null;

  if (justInstalled) {
    return (
      <div style={{
        display: "inline-flex", alignItems: "center", gap: 8,
        background: "rgba(16,185,129,0.12)", color: "#10B981",
        border: "1px solid rgba(16,185,129,0.35)", borderRadius: 14,
        padding: "12px 20px", fontWeight: 700, fontSize: 14,
        animation: "fadeIn 0.3s ease",
      }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2.5" strokeLinecap="round">
          <polyline points="20 6 9 17 4 12" />
        </svg>
        Aplicación instalada correctamente
      </div>
    );
  }

  if (!canInstall) return null;

  return (
    <button
      onClick={handleClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      aria-label="Instalar aplicación CuadraAI"
      style={{
        display: "inline-flex", alignItems: "center", gap: 8,
        background: hover ? C.primaryDark : C.primary, color: "#fff",
        border: "none", borderRadius: 14, padding: "13px 22px",
        fontWeight: 800, fontSize: 14, fontFamily: "inherit", cursor: "pointer",
        boxShadow: `0 8px 24px ${C.primary}45`, transition: "all 0.2s",
      }}
    >
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 3v12m0 0l-4-4m4 4l4-4M4 17v2a2 2 0 002 2h12a2 2 0 002-2v-2" />
      </svg>
      Instalar aplicación
    </button>
  );
}
